<script setup>
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import TaskCard from './TaskCard.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  column: {
    type: Object,
    required: true,
  },
  canDelete: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  'change',
  'openCard',
  'addCard',
  'configure',
  'rename',
  'remove',
]);

const { t } = useI18n();

const isAdding = ref(false);
const newTitle = ref('');
const titleInput = ref(null);

const isMenuOpen = ref(false);
const isRenaming = ref(false);
const renameValue = ref('');
const renameInput = ref(null);

const color = computed(() => props.column.color || '#64748b');
const count = computed(() => props.column.cards.length);
const wipLimit = computed(() => Number(props.column.wip_limit) || 0);
const isOverWip = computed(
  () => wipLimit.value > 0 && count.value > wipLimit.value
);
const isAtWip = computed(
  () => wipLimit.value > 0 && count.value === wipLimit.value
);
const countLabel = computed(() =>
  wipLimit.value ? `${count.value}/${wipLimit.value}` : `${count.value}`
);

const menuItems = computed(() => [
  {
    label: t('TASKS.RENAME'),
    action: 'rename',
    value: 'rename',
    icon: 'i-lucide-pencil',
  },
  {
    label: t('TASKS.CONFIGURE_PIPELINE'),
    action: 'configure',
    value: 'configure',
    icon: 'i-lucide-settings-2',
  },
  {
    label: t('TASKS.DELETE_COLUMN'),
    action: 'delete',
    value: 'delete',
    icon: 'i-lucide-trash-2',
    disabled: !props.canDelete,
  },
]);

// Cores dinâmicas por coluna (padrão do CRM). color-mix com `transparent` deixa
// os tons translúcidos e legíveis nos temas claro e escuro.
const columnStyle = computed(() => ({
  backgroundColor: `color-mix(in srgb, ${color.value} 7%, transparent)`,
  borderColor: `color-mix(in srgb, ${color.value} 22%, transparent)`,
}));
const headerStyle = computed(() => ({
  backgroundColor: `color-mix(in srgb, ${color.value} 16%, transparent)`,
}));
const countStyle = computed(() =>
  isOverWip.value
    ? {
        backgroundColor: 'color-mix(in srgb, #ef4444 22%, transparent)',
        color: '#ef4444',
      }
    : {
        backgroundColor: `color-mix(in srgb, ${color.value} 24%, transparent)`,
        color: color.value,
      }
);

const startAdding = async () => {
  isAdding.value = true;
  newTitle.value = '';
  await nextTick();
  titleInput.value?.focus();
};

const cancelAdding = () => {
  isAdding.value = false;
  newTitle.value = '';
};

const submitCard = () => {
  const title = newTitle.value.trim();
  if (!title) return;
  emit('addCard', { columnId: props.column.id, title });
  newTitle.value = '';
};

const startRenaming = async () => {
  isMenuOpen.value = false;
  isRenaming.value = true;
  renameValue.value = props.column.name;
  await nextTick();
  renameInput.value?.focus();
};

const saveRename = () => {
  const name = renameValue.value.trim();
  isRenaming.value = false;
  if (name && name !== props.column.name) {
    emit('rename', { columnId: props.column.id, name });
  }
};

const onMenuAction = ({ action }) => {
  isMenuOpen.value = false;
  if (action === 'rename') startRenaming();
  else if (action === 'configure') emit('configure');
  else if (action === 'delete') emit('remove', props.column.id);
};
</script>

<template>
  <div
    class="flex flex-col rounded-xl w-[19rem] shrink-0 max-h-full border"
    :style="columnStyle"
  >
    <div
      class="flex flex-col gap-1 px-3 py-2.5 border-b rounded-t-xl border-n-weak/60"
      :style="headerStyle"
    >
      <div class="flex items-center gap-2">
        <span
          class="rounded-full size-2.5 shrink-0 ring-2 ring-white/70 dark:ring-black/30"
          :style="{ backgroundColor: color }"
        />
        <input
          v-if="isRenaming"
          ref="renameInput"
          v-model="renameValue"
          type="text"
          class="flex-1 min-w-0 px-1.5 py-0.5 text-sm font-semibold border rounded-md outline-none bg-n-alpha-black2 text-n-slate-12 border-n-brand"
          @keydown.enter.prevent="saveRename"
          @keydown.esc="isRenaming = false"
          @blur="saveRename"
        />
        <h3 v-else class="text-sm font-semibold truncate text-n-slate-12">
          {{ column.name }}
        </h3>
        <span
          class="px-1.5 py-0.5 text-xs font-semibold rounded-full tabular-nums transition-colors"
          :style="countStyle"
        >
          {{ countLabel }}
        </span>
        <div
          v-on-clickaway="() => (isMenuOpen = false)"
          class="relative ml-auto"
        >
          <button
            class="p-0.5 rounded transition-opacity opacity-0 group-hover/board:opacity-60 hover:!opacity-100 text-n-slate-11"
            :class="{ '!opacity-100': isMenuOpen }"
            :title="t('TASKS.COLUMN_ACTIONS')"
            @click="isMenuOpen = !isMenuOpen"
          >
            <span class="i-lucide-ellipsis size-4" />
          </button>
          <DropdownMenu
            v-if="isMenuOpen"
            :menu-items="menuItems"
            class="right-0 mt-1 top-full"
            @action="onMenuAction"
          />
        </div>
      </div>
      <p
        v-if="isOverWip"
        class="flex items-center gap-1 text-xs font-medium text-n-ruby-11"
      >
        <span class="i-lucide-triangle-alert size-3" />
        {{ t('TASKS.WIP_EXCEEDED') }}
      </p>
      <p v-else-if="isAtWip" class="text-xs text-n-slate-10">
        {{ t('TASKS.WIP_REACHED') }}
      </p>
    </div>

    <Draggable
      :list="column.cards"
      group="tasks"
      item-key="id"
      class="flex flex-col gap-2 px-2.5 py-2.5 overflow-y-auto grow min-h-4"
      ghost-class="opacity-40"
      drag-class="rotate-2"
      @change="emit('change', { columnId: column.id, event: $event })"
    >
      <template #item="{ element }">
        <TaskCard :task="element" @open="emit('openCard', $event)" />
      </template>
      <template #footer>
        <p
          v-if="!column.cards.length && !isAdding"
          class="py-6 text-xs text-center text-n-slate-10"
        >
          {{ t('TASKS.EMPTY_COLUMN') }}
        </p>
      </template>
    </Draggable>

    <div class="p-2">
      <form
        v-if="isAdding"
        class="flex flex-col gap-2"
        @submit.prevent="submitCard"
      >
        <textarea
          ref="titleInput"
          v-model="newTitle"
          rows="2"
          :placeholder="t('TASKS.CARD_TITLE')"
          class="w-full px-2 py-1.5 text-sm border rounded-lg outline-none resize-none bg-n-solid-1 text-n-slate-12 border-n-weak focus:border-n-brand"
          @keydown.enter.exact.prevent="submitCard"
        />
        <div class="flex items-center gap-2">
          <button
            type="submit"
            class="px-3 py-1 text-xs font-medium text-white rounded-md bg-n-brand hover:bg-n-brand/90"
          >
            {{ t('TASKS.ADD_CARD') }}
          </button>
          <button
            type="button"
            class="px-2 py-1 text-xs rounded-md text-n-slate-11 hover:bg-n-alpha-2"
            @click="cancelAdding"
          >
            {{ t('TASKS.CANCEL') }}
          </button>
        </div>
      </form>
      <button
        v-else
        class="flex items-center w-full gap-1.5 px-2 py-1.5 text-xs font-medium rounded-md text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
        @click="startAdding"
      >
        <span class="i-lucide-plus size-3.5" />
        {{ t('TASKS.ADD_CARD') }}
      </button>
    </div>
  </div>
</template>
